// =============================================================================
// Leitura do modelo anual de orçamento (.xlsx)
//
// O arquivo do Financeiro tem duas fontes de orçamento:
//   - MOVIMENTACOES: conta × CC × histórico × mês;
//   - FOPAG: custo mensal por pessoa.
//
// Da FOPAG só atravessa esta fronteira o agregado conta × CC × mês. Nome,
// cargo, remuneração e benefícios individuais nunca entram no payload nem na
// prévia. Linha sem `CONTA V2 (PJ)` — hoje os CLT e duas sem vínculo — é
// contada como pendência, mas não é importada: escolher uma categoria por conta
// própria produziria um orçamento errado.
// =============================================================================

import type { SheetData } from "read-excel-file/browser";
import type { LinhaPlanilha } from "./planilha.ts";

export type OrigemOrcamentoXlsx = "MOVIMENTACOES" | "FOPAG";

export type LinhaOrcamentoXlsx = LinhaPlanilha & {
  origem_xlsx: OrigemOrcamentoXlsx;
  linha_xlsx: number | null;
  categoria_codigo: string;
};

export type ResumoAbaXlsx = {
  encontrada: boolean;
  linhas: number;
  grupos: number;
  ignoradas: number;
  aviso?: string;
};

export type LeituraOrcamentoXlsx = {
  nomeArquivo: string;
  linhas: LinhaOrcamentoXlsx[];
  movimentacoes: ResumoAbaXlsx;
  fopag: ResumoAbaXlsx & {
    pessoasComConta: number;
    pessoasSemConta: number;
    pessoasCltSemConta: number;
  };
  erros: string[];
};

type Aba = { sheet: string; data: SheetData };

const MESES = ["JAN", "FEV", "MAR", "ABR", "MAI", "JUN", "JUL", "AGO", "SET", "OUT", "NOV", "DEZ"];

function texto(valor: unknown): string {
  if (valor === null || valor === undefined) return "";
  if (typeof valor === "number" && Number.isInteger(valor)) return String(valor);
  return String(valor).trim();
}

function normalizar(valor: unknown): string {
  return texto(valor)
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .toUpperCase()
    .trim();
}

function numero(valor: unknown): number | null {
  if (typeof valor === "number" && Number.isFinite(valor)) return valor;
  if (typeof valor !== "string") return null;
  const limpo = valor.trim().replace(/\s/g, "");
  if (!limpo) return null;
  const canonico = limpo.includes(",")
    ? limpo.replace(/\./g, "").replace(",", ".")
    : limpo;
  const n = Number(canonico);
  return Number.isFinite(n) ? n : null;
}

function acharCabecalho(dados: SheetData, obrigatorios: string[]): { linha: number; colunas: Map<string, number> } | null {
  for (let linha = 0; linha < Math.min(dados.length, 15); linha++) {
    const colunas = new Map<string, number>();
    for (let coluna = 0; coluna < (dados[linha]?.length ?? 0); coluna++) {
      const nome = normalizar(dados[linha]?.[coluna]);
      if (nome && !colunas.has(nome)) colunas.set(nome, coluna);
    }
    if (obrigatorios.every((nome) => colunas.has(nome))) return { linha, colunas };
  }
  return null;
}

function mapaPlano(dados: SheetData | undefined): Map<string, string> {
  const resultado = new Map<string, string>();
  if (!dados) return resultado;
  const cab = acharCabecalho(dados, ["CODIGO V2", "NOME DA CATEGORIA"]);
  if (!cab) return resultado;
  const iCodigo = cab.colunas.get("CODIGO V2")!;
  const iNome = cab.colunas.get("NOME DA CATEGORIA")!;
  for (const linha of dados.slice(cab.linha + 1)) {
    const codigo = texto(linha[iCodigo]);
    const nome = texto(linha[iNome]);
    if (codigo && nome) resultado.set(codigo, nome);
  }
  return resultado;
}

export function interpretarMovimentacoes(dados: SheetData | undefined, plano: Map<string, string>): {
  linhas: LinhaOrcamentoXlsx[];
  resumo: ResumoAbaXlsx;
  erros: string[];
} {
  if (!dados) {
    return {
      linhas: [], erros: ["A aba MOVIMENTACOES não foi encontrada."],
      resumo: { encontrada: false, linhas: 0, grupos: 0, ignoradas: 0 },
    };
  }
  const cab = acharCabecalho(dados, ["CODIGO V2", "CC", "HISTORICO", ...MESES]);
  if (!cab) {
    return {
      linhas: [], erros: ["A aba MOVIMENTACOES não tem o cabeçalho esperado (Código V2, CC, Histórico e JAN a DEZ)."],
      resumo: { encontrada: true, linhas: 0, grupos: 0, ignoradas: 0 },
    };
  }

  const linhas: LinhaOrcamentoXlsx[] = [];
  let linhasFonte = 0;
  let ignoradas = 0;
  for (let i = cab.linha + 1; i < dados.length; i++) {
    const linha = dados[i]!;
    const codigo = texto(linha[cab.colunas.get("CODIGO V2")!]);
    const cc = texto(linha[cab.colunas.get("CC")!]);
    const historico = texto(linha[cab.colunas.get("HISTORICO")!]);
    if (!codigo && !cc && !historico) continue;
    linhasFonte++;
    let geradas = 0;
    for (let mes = 0; mes < 12; mes++) {
      const bruto = linha[cab.colunas.get(MESES[mes]!)!];
      const valor = numero(bruto);
      if (bruto === null || bruto === undefined || bruto === "" || valor === 0) continue;
      geradas++;
      linhas.push({
        centro_custo: cc,
        categoria: plano.get(codigo) ?? "",
        categoria_codigo: codigo,
        tipo: "pontual",
        valor: valor === null ? texto(bruto) : String(valor),
        competencia: String(mes + 1),
        subcategoria: historico,
        origem_xlsx: "MOVIMENTACOES",
        linha_xlsx: i + 1,
      });
    }
    if (geradas === 0) ignoradas++;
  }
  return {
    linhas,
    erros: [],
    resumo: { encontrada: true, linhas: linhasFonte, grupos: linhas.length, ignoradas },
  };
}

export function interpretarFopag(dados: SheetData | undefined, plano: Map<string, string>): {
  linhas: LinhaOrcamentoXlsx[];
  resumo: LeituraOrcamentoXlsx["fopag"];
  erros: string[];
} {
  if (!dados) {
    return {
      linhas: [], erros: ["A aba FOPAG não foi encontrada."],
      resumo: {
        encontrada: false, linhas: 0, grupos: 0, ignoradas: 0,
        pessoasComConta: 0, pessoasSemConta: 0, pessoasCltSemConta: 0,
      },
    };
  }
  const cab = acharCabecalho(dados, ["CC", "NOME", "CONTA V2 (PJ)", ...MESES]);
  if (!cab) {
    return {
      linhas: [], erros: ["A aba FOPAG não tem o cabeçalho esperado (CC, Nome, Conta V2 e JAN a DEZ)."],
      resumo: {
        encontrada: true, linhas: 0, grupos: 0, ignoradas: 0,
        pessoasComConta: 0, pessoasSemConta: 0, pessoasCltSemConta: 0,
      },
    };
  }

  const iCc = cab.colunas.get("CC")!;
  const iNome = cab.colunas.get("NOME")!;
  const iConta = cab.colunas.get("CONTA V2 (PJ)")!;
  const iVinculo = cab.colunas.get("CLT/PJ");
  const agregados = new Map<string, { codigo: string; cc: string; mes: number; valor: number }>();
  let pessoasComConta = 0;
  let pessoasSemConta = 0;
  let pessoasCltSemConta = 0;

  for (let i = cab.linha + 1; i < dados.length; i++) {
    const linha = dados[i]!;
    // O bloco seguinte é um resumo por CC e repete os valores. Parar aqui evita
    // importar a folha duas vezes, sem depender de um número fixo de linha.
    if (linha.some((celula) => normalizar(celula).includes("RESUMO POR CENTRO DE CUSTO"))) break;

    const cc = texto(linha[iCc]);
    const temPessoa = texto(linha[iNome]) !== "";
    if (!temPessoa || !/^\d+$/.test(cc)) continue;
    const codigo = texto(linha[iConta]);
    if (!codigo.startsWith("2.")) {
      pessoasSemConta++;
      if (iVinculo !== undefined && normalizar(linha[iVinculo]) === "CLT") pessoasCltSemConta++;
      continue;
    }
    pessoasComConta++;
    for (let mes = 0; mes < 12; mes++) {
      const valor = numero(linha[cab.colunas.get(MESES[mes]!)!]);
      if (valor === null || Math.abs(valor) <= 0.005) continue;
      const chave = `${codigo}|${cc}|${mes}`;
      const atual = agregados.get(chave);
      if (atual) atual.valor += valor;
      else agregados.set(chave, { codigo, cc, mes, valor });
    }
  }

  const linhas = [...agregados.values()].map<LinhaOrcamentoXlsx>((g) => ({
    centro_custo: g.cc,
    categoria: plano.get(g.codigo) ?? "",
    categoria_codigo: g.codigo,
    tipo: "pontual",
    valor: String(Math.round(g.valor * 100) / 100),
    competencia: String(g.mes + 1),
    subcategoria: "",
    origem_xlsx: "FOPAG",
    // O grupo pode vir de várias pessoas; exibir uma linha individual seria
    // enganoso e facilitaria vazar a origem do agregado.
    linha_xlsx: null,
  }));

  return {
    linhas,
    erros: [],
    resumo: {
      encontrada: true,
      linhas: pessoasComConta + pessoasSemConta,
      grupos: linhas.length,
      ignoradas: pessoasSemConta,
      pessoasComConta,
      pessoasSemConta,
      pessoasCltSemConta,
      aviso: pessoasSemConta > 0
        ? `${pessoasSemConta} linha(s) ficaram fora por falta de conta contábil (${pessoasCltSemConta} marcadas como CLT).`
        : undefined,
    },
  };
}

export async function lerOrcamentoXlsx(arquivo: File): Promise<LeituraOrcamentoXlsx> {
  // Carrega o leitor só quando alguém escolhe um .xlsx. A biblioteca e o ZIP
  // do Excel não aumentam o JavaScript inicial das demais telas.
  const { default: readExcelFile } = await import("read-excel-file/browser");
  const abas = await readExcelFile(arquivo) as Aba[];
  const porNome = new Map(abas.map((aba) => [normalizar(aba.sheet), aba.data]));
  const plano = mapaPlano(porNome.get("PLANO_CONTAS"));
  const erros: string[] = [];
  if (plano.size === 0) erros.push("A aba PLANO_CONTAS não foi encontrada ou não tem Código V2 e Nome da categoria.");

  const mov = interpretarMovimentacoes(porNome.get("MOVIMENTACOES"), plano);
  const folha = interpretarFopag(porNome.get("FOPAG"), plano);
  erros.push(...mov.erros, ...folha.erros);
  return {
    nomeArquivo: arquivo.name,
    linhas: [...mov.linhas, ...folha.linhas],
    movimentacoes: mov.resumo,
    fopag: folha.resumo,
    erros,
  };
}
